import {Injectable} from "@angular/core";
import {EventService} from "@app/core/tracking/application/event-service";
import {Event} from "@app/core/tracking/domain/event";
import {ListsViewedEvent} from "@app/core/tracking/application/events/lists-viewed-event";
import {IsLoggedInUseCase} from "@app/core/user/application/isLoggedIn.use-case";
import {firstValueFrom} from "rxjs";

/**
 * Use case for tracking events.
 */
@Injectable({
    providedIn: 'root'
})
export class TrackEventUseCase {
    constructor(
        private eventService: EventService,
        private isLoggedInUseCase: IsLoggedInUseCase
    ) {}

    /**
     * Executes the event tracking process.
     * @param event The event to be tracked.
     * @returns A promise that resolves when the event is processed.
     */
    async execute(event: Event): Promise<void> {
        // Ensure the event meets the required conditions before sending it.
        if (!(await this.canSendEvent(event))) {
            return;
        }
        this.eventService.notify(event);
    }

    /**
     * Determines whether the given event should be sent.
     * @param event The event to be evaluated.
     * @returns A promise resolving to `true` if the event can be sent, otherwise `false`.
     */
    private async canSendEvent(event: Event): Promise<boolean> {
        if (event instanceof ListsViewedEvent) {
            /* The `ListsViewedEvent` is triggered when the user navigates to the lists screen.
               If the user is not logged in, they see a message prompting them to log in,
               but the event is still emitted. However, we only want to send the event if the user is logged in.*/
            return await firstValueFrom(this.isLoggedInUseCase.execute());
        }
        return true;
    }
}